// @ts-nocheck

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StudiesState, StudyDetail, StudyYearRef } from '@/types/studies';
import { studies } from './thunks';

const initialState: StudiesState = {
  isLoading: false,
  error: null,
  studies: [],
  studyId: '',
  studyName: '',
  studyProfile: {} as StudyDetail,
  yearSelected: null,
  yearProfile: null,
  company: [],
  studyCompanies: [],
  studyYearCompanies: [],
  studyYearChanges: [],
  studyHistory: [],
  studyHistoryChartData: { primary_study: [], peer_studies: [] },
  categoryMarketData: [],
  createdStudy: null,
  hasMore: true,
  offset: 0,
  limit: 100,
  isLoadingMore: false,
  total: 0,
  currentStudy: null,
};

export const studiesSlice = createSlice({
  name: 'studies',
  initialState,
  reducers: {
    setStudyId: (state, action: PayloadAction<string>) => {
      state.studyId = action.payload;
    },
    setStudyName: (state, action: PayloadAction<string>) => {
      state.studyName = action.payload;
    },
    resetStudyId: state => {
      state.studyId = '';
      state.studyName = '';
    },
    setYearSelected: (state, action: PayloadAction<StudyYearRef>) => {
      state.yearSelected = action.payload;
    },
    resetYearSelected: state => {
      state.yearSelected = null;
    },
    clearCreatedStudy: state => {
      state.createdStudy = null;
    },
    clearStudyData: state => {
      state.studyData = [];
    },
    setCurrentStudy: (state, action: PayloadAction<StudyRef | null>) => {
      state.currentStudy = action.payload;
    },
    resetPagination: state => {
      state.offset = 0;
      state.hasMore = true;
      state.studies = [];
    },
  },
  extraReducers: builder => {
    builder
      // --------------------------------------- V3 - MIGRATED ------------------------------------------

      // getAllStudies (V3)
      .addCase(studies.getAllStudies.pending, (state, action) => {
        if (action.meta.arg?.offset === 0 || !action.meta.arg?.offset) {
          state.isLoading = true;
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(studies.getAllStudies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = null;
        const { offset = 0 } = action.payload;

        if (offset === 0) {
          state.studies = action.payload.data;
          state.offset = action.payload.data.length;
        } else {
          state.studies = [...state.studies, ...action.payload.data];
          state.offset = state.offset + action.payload.data.length;
        }

        state.hasMore = state.studies.length < action.payload.total;

        console.log('Redux updated:', {
          totalStudies: state.studies.length,
          newOffset: state.offset,
          hasMore: state.hasMore,
          receivedCount: action.payload.data.length,
          totalAvailable: action.payload.total,
        });
      })
      .addCase(studies.getAllStudies.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      })

      // getStudyProfile (V3)
      .addCase(studies.getStudyProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getStudyProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studyProfile = action.payload;
        state.error = null;
        if (!state.yearSelected && action.payload.years_available_data?.length > 0) {
          const mostRecentYear = action.payload.years_available_data
            .slice()
            .sort((a, b) => b.year - a.year)[0];

          if (mostRecentYear) {
            // set selectedYear as partial object -- // then fetch studyYearProfile based on partial object.
            console.log('mostRecentYear', mostRecentYear);
            state.yearSelected = mostRecentYear;
          }
        }
      })
      .addCase(studies.getStudyProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // updateStudy (V3)
      .addCase(studies.updateStudy.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.updateStudy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studyProfile = action.payload;
        // Update study in studies array
        const index = state.studies.findIndex(study => study.id === action.payload.id);
        if (index !== -1) {
          state.studies[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(studies.updateStudy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // createStudy (V3)
      .addCase(studies.createStudy.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.createStudy.fulfilled, (state, action) => {
        state.createdStudy = action.payload;
        state.studies.push(action.payload);
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studies.createStudy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // deleteStudy (V3)
      .addCase(studies.deleteStudy.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.deleteStudy.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove from studies array - need to pass studyId in meta or use different approach
        state.error = null;
      })
      .addCase(studies.deleteStudy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getStudyData (V3)
      .addCase(studies.getStudyData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getStudyData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studyData = action.payload;
        state.error = null;
      })
      .addCase(studies.getStudyData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // --------------------------------------- V2 - LEGACY SUPPORT ------------------------------------------

      // getStudyYearProfileV2
      // .addCase(studies.getStudyYearProfileV2.pending, state => {
      //   state.isLoading = true;
      //   state.error = null;
      // })
      // .addCase(studies.getStudyYearProfileV2.fulfilled, (state, action) => {
      //   state.isLoading = false;
      //   state.yearProfile = action.payload;
      //   state.error = null;
      // })
      // .addCase(studies.getStudyYearProfileV2.rejected, (state, action) => {
      //   state.isLoading = false;
      //   state.error = action.payload as string;
      // })

      // getStudyYearCompaniesV2
      .addCase(studies.getStudyYearCompaniesV2.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getStudyYearCompaniesV2.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studyYearCompanies = action.payload;
        state.error = null;
      })
      .addCase(studies.getStudyYearCompaniesV2.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getCategoryMarketData
      .addCase(studies.getCategoryMarketData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getCategoryMarketData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categoryMarketData = action.payload;
        state.error = null;
      })
      .addCase(studies.getCategoryMarketData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // --------------------------------------- LEGACY METHODS ------------------------------------------

      // getStudiesCompaniesData
      .addCase(studies.getStudiesCompaniesData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getStudiesCompaniesData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.company = action.payload;
        state.error = null;
      })
      .addCase(studies.getStudiesCompaniesData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getStudyCompanies
      .addCase(studies.getStudyCompanies.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getStudyCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studyCompanies = action.payload;
        state.error = null;
      })
      .addCase(studies.getStudyCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getStudyHistory
      .addCase(studies.getStudyHistory.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studies.getStudyHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studyHistoryChartData = action.payload;
        state.error = null;
      })
      .addCase(studies.getStudyHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setStudyId,
  setStudyName,
  setYearSelected,
  resetStudyId,
  clearCreatedStudy,
  clearStudyData,
  resetYearSelected,
  setCurrentStudy,
  resetPagination,
} = studiesSlice.actions;

export const studiesReducer = studiesSlice.reducer;
